import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import useFocusTrap from '../../hooks/useFocusTrap';
import './WelcomeModal.css';

const WELCOME_KEY = 'hasSeenWelcome';

function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const { isAuthenticated, loading } = useAuth();
  const { hasSelectedTheme } = useTheme();
  const navigate = useNavigate();
  const focusTrapRef = useFocusTrap(open);

  useEffect(() => {
    // Only show for authenticated users who have selected theme but haven't seen welcome
    if (loading || !isAuthenticated || !hasSelectedTheme) {
      return;
    }

    const hasSeenWelcome = localStorage.getItem(WELCOME_KEY) === 'true';
    if (!hasSeenWelcome) {
      setOpen(true);
    }
  }, [isAuthenticated, loading, hasSelectedTheme]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow || 'unset';
    };
  }, [open]);

  const handleClose = () => {
    localStorage.setItem(WELCOME_KEY, 'true');
    setOpen(false);
  };

  const handleTakeTour = () => {
    localStorage.setItem(WELCOME_KEY, 'true');
    setOpen(false);
    navigate('/how-to-use');
  };

  if (!open) return null;

  return (
    <div className="modal-overlay welcome-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="welcome-modal-title">
      <div className="modal-content welcome-modal" ref={focusTrapRef}>
        <div className="modal-body welcome-modal-body">
          <div className="welcome-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48" aria-hidden="true">
              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 id="welcome-modal-title" className="welcome-title">Welcome to Options Tracker!</h2>
          <p className="welcome-subtitle">
            Track your options trades, monitor positions, and analyze your performance all in one place.
          </p>
          <p className="welcome-description">
            New here? We have a quick guide to help you get started with setting up accounts and logging your first trades.
          </p>
        </div>

        <div className="modal-footer welcome-modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>
            Skip for Now
          </button>
          <button className="btn btn-primary" onClick={handleTakeTour}>
            Show Me How
          </button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeModal;
